/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph, {ERenderFiler} from "App/render/webgl/render_graph";
import Factory from "App/render/webgl/passes/factory";
import View from "App/view/view";
import {Framebuffer} from "App/utils/frame_buffer";

import AlphaTransparencyShader from "App/render/webgl/shaders/alpha_transparency";
import BillboardShader from "App/render/webgl/shaders/billboard";
import LineShader from "App/render/webgl/shaders/line";
import OpaqueShader from "App/render/webgl/shaders/opaque";
import OpaqueNoTextureShader from "App/render/webgl/shaders/opaque_no_texture";
import OutlineShader from "App/render/webgl/shaders/outline";
import ParticlesShader from "App/render/webgl/shaders/particles";
import PointShader from "App/render/webgl/shaders/point";
import PointCloudShader from "App/render/webgl/shaders/point_cloud";
import ShadowVolumeShader from "App/render/webgl/shaders/shadow_volume/shadow_volume";
import SkyBoxShader from "App/render/webgl/shaders/sky_box";
import SkyDomeShader from "App/render/webgl/shaders/sky_dome";
import ChunkLODNodeOpaqueShader from "App/render/webgl/shaders/chunk_lod_node_opaque";
import HUDAlphaTransparencyShader from "App/render/webgl/shaders/hud_alpha_transparency";
import {RenderOptionNodeTypeName} from "App/render/webgl/nodes/type_names";
import {
    AlphaTransparencyBinTypeName,
    BillboardBinTypeName,
    ChunkLODBinTypeName,
    LinesBinTypeName,
    NewHUDBinTypeName,
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName,
    OutlinesBinTypeName,
    ParticlesBinTypeName,
    PoiBinTypeName,
    PointCloudsBinTypeName,
    PointsBinTypeName,
    ShadowVolumesBinTypeName,
    SkyBoxBinTypeName,
    SkyDomeBinTypeName,
    TextBinTypeName,
    TransparentBinTypeName,
    TransparentInternalBinTypeName,
    TransparentNoTextureBinTypeName,
    TransparentNoTextureInternalBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import {
    ScreenPassAllTransparentTypeName,
    ScreenPassTypeName,
    WeightedAverageTransparencyPassTypeName
} from "App/render/webgl/passes/type_names";
import IScene from "js_interface/src/scene_interface";

/**
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @extends {Pass}
 * @constructor
 */
export default
class ScreenAllTransparent extends Pass {

    public static TypeName: string = ScreenPassAllTransparentTypeName;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this._requireLighting = true;
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ScreenAllTransparent {
        return new ScreenAllTransparent(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(AlphaTransparencyBinTypeName, shaders.get(AlphaTransparencyShader.TypeName));
        this.attachShaderToBin(BillboardBinTypeName, shaders.get(BillboardShader.TypeName));
        this.attachShaderToBin(LinesBinTypeName, shaders.get(LineShader.TypeName));
        // this.attachShaderToBin(OpaqueBinTypeName, shaders.get(OpaqueShader.TypeName));
        // this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(OpaqueNoTextureShader.TypeName));
        this.attachShaderToBin(OutlinesBinTypeName, shaders.get(OutlineShader.TypeName));
        this.attachShaderToBin(ParticlesBinTypeName, shaders.get(ParticlesShader.TypeName));
        this.attachShaderToBin(PoiBinTypeName, shaders.get(PointShader.TypeName));
        this.attachShaderToBin(PointCloudsBinTypeName, shaders.get(PointCloudShader.TypeName));
        this.attachShaderToBin(PointsBinTypeName, shaders.get(PointShader.TypeName));
        this.attachShaderToBin(ShadowVolumesBinTypeName, shaders.get(ShadowVolumeShader.TypeName));
        this.attachShaderToBin(SkyBoxBinTypeName, shaders.get(SkyBoxShader.TypeName));
        this.attachShaderToBin(SkyDomeBinTypeName, shaders.get(SkyDomeShader.TypeName));
        this.attachShaderToBin(TextBinTypeName, shaders.get(OpaqueNoTextureShader.TypeName));
        this.attachShaderToBin(ChunkLODBinTypeName, shaders.get(ChunkLODNodeOpaqueShader.TypeName));
        this.attachShaderToBin(NewHUDBinTypeName, shaders.get(HUDAlphaTransparencyShader.TypeName));
    }

    private _renderTransparentObjects(aView: View) {
        // test whether there is something to render inside
        let bins = this._renderGraph.getRenderBins();
        if (!bins.get(TransparentInternalBinTypeName).hasChildren() &&
            !bins.get(TransparentNoTextureInternalBinTypeName).hasChildren() &&
            !bins.get(TransparentBinTypeName).hasChildren() &&
            !bins.get(TransparentNoTextureBinTypeName).hasChildren()) {
            return;
        }

        // render transparent objects to color and depth acc buffers
        this._renderGraph.getPasses().get(WeightedAverageTransparencyPassTypeName).render(
            aView,
            this._renderGraph.getFramebuffers().opaqueDepth,
            this._renderGraph.getFramebuffers().colorAccumulation,
            this._renderGraph.getFramebuffers().depthAccumulation,
            this._renderGraph.getFramebuffers().screen
        );
    }

    public render(aView: View, aScreenBuffer: Framebuffer) {
        let scale = aScreenBuffer.getParameters().scale;
        let gl = this._context.getWebGLRenderingContext();
        let camera = aView.getControls().getCamera();

        // set rendering target
        aScreenBuffer.bind();

        let position = aView.getPosition();
        let size = aView.getSize();

        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        let bins = this._renderGraph.getRenderBins();
        this.view = aView;

        if (this._sceneAPI.renderSettingsModule.getRenderXRay()) {
            gl.disable(gl.DEPTH_TEST);
            gl.blendFunc(gl.SRC_ALPHA, gl.DST_ALPHA);
            gl.enable(gl.BLEND);
        }

        // we have to render the skybox first otherwise it rewrites the objects with depth test off e.g. dynamic picking line
        let prevCameraUseOrtho = camera.setUseOrthographicProjection(false);
        bins.get(SkyBoxBinTypeName).render(this);
        bins.get(SkyDomeBinTypeName).render(this);
        camera.setUseOrthographicProjection(prevCameraUseOrtho);

        //bins.get(OpaqueBinTypeName).render(this);
        //bins.get(OpaqueNoTextureBinTypeName).render(this);
        bins.get(ChunkLODBinTypeName).render(this);
        bins.get(PointCloudsBinTypeName).render(this);
        bins.get(TextBinTypeName).render(this);
        bins.get(OutlinesBinTypeName).render(this);
        bins.get(LinesBinTypeName).render(this);
        bins.get(PoiBinTypeName).render(this);
        bins.get(PointsBinTypeName).render(this);

        // alpha transparency, enable blend with depth test on
        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        bins.get(AlphaTransparencyBinTypeName).render(this);
        gl.disable(gl.BLEND);

        bins.get(BillboardBinTypeName).render(this);
        bins.get(ParticlesBinTypeName).render(this);

        if (this._sceneAPI.renderSettingsModule.getRenderXRay()) {
            gl.enable(gl.DEPTH_TEST);
            gl.disable(gl.BLEND);
            gl.depthFunc(gl.LEQUAL);
        }

        this._renderTransparentObjects(aView);

        // draw the always on top geometry
        gl.disable(gl.DEPTH_TEST);

        this.state.setRenderNodesFilters([{
            type: RenderOptionNodeTypeName,
            value: ERenderFiler.alwaysOnTop
        }
        ]);
        bins.get(LinesBinTypeName).render(this);
        bins.get(OutlinesBinTypeName).render(this);
        bins.get(PointsBinTypeName).render(this);

        this.state.resetRenderNodesFilters();

        gl.enable(gl.DEPTH_TEST);

        gl.clear(gl.DEPTH_BUFFER_BIT);

        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        // draw newHud objects with depth test enabled, but with scissor test
        bins.get(NewHUDBinTypeName).render(this);

        gl.disable(gl.BLEND);

        gl.disable(gl.SCISSOR_TEST);

        aScreenBuffer.unbind(this._typeName);

        this.view = null;
    }
}

Factory.register(ScreenAllTransparent);
